import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorNotify from "../../components/ErrorNotify/ErrorNotify";
import FeedbackNPS from "../../components/FeedbackNPS/FeedbackNPS";
import { Form } from "../../components/Form/Form";
import MainModal from "../../components/Modal/Modal";
import { Tabs } from "../../components/Tabs";
import type { IRootState } from "../../redux/store";
import {
	BrainData,
	type TTab,
	VoiceTypes,
	fetchBrainData,
	tabsList,
} from "../../utils";
import style from "./Avatar.module.scss";
import routes from "../../routs";
import { useDispatch, useSelector } from "react-redux";
import PreviewAvatarSection from "../../components/PreviewAvatarSection/PreviewAvatarSection";
import Spinner from "../../components/Spinner/Spinner";
import DeployAvatarModal from "../../components/DeployAvatarModal/DeployAvatarModal";
import PreviewAvatarMedia from "../../components/PreviewAvatarMedia/PreviewAvatarMedia";
import { setBrainId } from "../../redux/slices";

const AvatarPage = () => {
	const [activeTab, setActiveTab] = useState<TTab | null>("appearance");
	const [modal, setModal] = useState<
		"error" | "none" | "feedback" | "copyLink"
	>("none");
	const [isPublished, setIsPublished] = useState<boolean>(false);
	const memoizedTabsList = useMemo(() => tabsList, []);
	const availableVoices: VoiceTypes[] | [] = useSelector((state: IRootState) => state.voicesList);
	const formData = useSelector((state: IRootState) => state.avatarForm);
	const isPreviewSection = activeTab === "preview";
	const [isError, setIsError] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const fetchBrainId = useCallback(() => {
        setIsError(false);
        setIsLoading(true);
        return fetchBrainData(formData.prompt)
            .then((res) => res.json())
            .then((json: BrainData) => {
                dispatch(setBrainId(json.brain_id));
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [formData.prompt]);

	useEffect(() => {
		if (!availableVoices.length) {
			navigate(routes.home);
		}
	}, []);

	useEffect(() => {
		fetchBrainId();
	}, [formData.prompt]);

	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		});
	}, [activeTab, isPublished]);

	useEffect(() => {
		if (isPublished) {
			setModal("copyLink")
		}
	}, [isPublished]);

	useEffect(() => {
		if (!formData.type) {
			navigate(routes.home);
		}
	}, [formData]);

	useEffect(() => {
		if (isError) {
			setModal("error");
		}
	}, [isError]);

	const avatarFullScreen =
		activeTab !== null && ["preview"].includes(activeTab);

	const renderModal = () => {
		if (modal === "none") return null;

		const modalComponents = {
			error: <ErrorNotify onClose={() => setModal("none")} />,
			feedback: <FeedbackNPS onClose={() => setModal("none")} />,
			copyLink: <DeployAvatarModal onClose={() => setModal("none")} setIsPublished={setIsPublished} handleClosePreview={handleClosePreview}/>,
		};

		return <MainModal>{modalComponents[modal]}</MainModal>;
	};

	const handleDeployAvatar = () => {
		setIsPublished(true);
	};

	const handleClosePreview = () => {
		setActiveTab("appearance");
	};

	const handlePreviewAvatar = () => {
		setActiveTab("preview");
	};

	if (isLoading) {
		return (
			<div className={style.spinnerWrapper}>
				<Spinner size={"medium"} />
			</div>
		);
	}

	return (
		<div className={style.pageContainer}>
			{modal !== "none" && renderModal()}
			<Tabs
				setActiveTab={setActiveTab}
				activeTab={activeTab}
				tabsList={memoizedTabsList}
			/>
			<div className={style.container}>
				<div className={style.formWrapper}>
					{!avatarFullScreen && activeTab && (
						<Form activeTab={activeTab} setActiveTab={setActiveTab} />
					)}
				</div>
				{/* {isPublished && <PublishedCard />} */}
				{!isPreviewSection && (
					<PreviewAvatarMedia handlePreviewAvatar={handlePreviewAvatar} />
				)}
				{isPreviewSection && (
					<PreviewAvatarSection handleDeployAvatar={handleDeployAvatar} />
				)}
			</div>
		</div>
	);
};

export default AvatarPage;
